import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CustomerPortal() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [form, setForm] = useState({ signer_name: '', signer_email: '', signer_title: '' });
  const [showSignForm, setShowSignForm] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/${token}`).then(res => {
      setProposal(res.data.proposal);
      setSections(res.data.sections || []);
      if (res.data.proposal.portal_status === 'accepted') setSigned(true);
      setLoading(false);
    }).catch(() => { setLoading(false); });
  }, [token]);

  const handleSign = async (accepted) => {
    setSigning(true);
    try {
      await axios.post(`${API_URL}/api/portal/${token}/sign`, { ...form, accepted });
      setSigned(true);
      toast.success(accepted ? 'Proposal accepted!' : 'Proposal declined');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setSigning(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-3 border-green-500 border-t-transparent" />
    </div>
  );

  if (!proposal) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
      <div className="text-center p-10 bg-white rounded-2xl shadow-lg border max-w-md">
        <i className="fa-solid fa-link-slash text-4xl text-gray-300 mb-4 block" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Link Invalid or Expired</h2>
        <p className="text-gray-500 text-sm">This proposal link is no longer valid. Please contact your solar representative.</p>
      </div>
    </div>
  );

  const enabled = sections.filter(s => s.enabled);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50/30" data-testid="customer-portal">
      <Toaster position="top-center" />

      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center"><i className="fa-solid fa-solar-panel text-white text-xs" /></div>
            <span className="font-bold text-gray-900 text-sm">{proposal.company_name || 'Samaritan Solar'}</span>
          </div>
          {!signed && !showSignForm && (
            <button onClick={() => setShowSignForm(true)} className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-xl text-sm font-semibold transition-all shadow-sm" data-testid="accept-proposal-btn">
              <i className="fa-solid fa-check mr-1.5" />Accept Proposal
            </button>
          )}
          {signed && (
            <span className="flex items-center gap-1.5 text-green-600 font-semibold text-sm"><i className="fa-solid fa-circle-check" />Accepted</span>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-500 rounded-2xl p-8 text-white text-center mb-8 shadow-lg">
          <h1 className="text-3xl font-bold mb-2">Solar Energy Proposal</h1>
          <p className="opacity-90">Prepared for <span className="font-semibold">{proposal.lead_name}</span></p>
          <p className="text-xs opacity-70 mt-1">{proposal.lead_address}, {proposal.lead_city}, {proposal.lead_state}</p>
        </div>

        <div className="space-y-6">
          {/* Property */}
          {enabled.find(s => s.type === 'property_assessment') && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><i className="fa-solid fa-house text-green-500" />Property Details</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[{l:'Property',v:proposal.property_type},{l:'Roof Age',v:`${proposal.roof_age||'N/A'} years`},{l:'Solar Potential',v:proposal.solar_potential},{l:'Annual Usage',v:`${(proposal.annual_kwh||0).toLocaleString()} kWh`},{l:'Location',v:`${proposal.lead_city}, ${proposal.lead_state}`},{l:'Contact',v:proposal.lead_email}].map(i=>(
                  <div key={i.l}><p className="text-xs text-gray-500 uppercase font-medium">{i.l}</p><p className="text-sm font-semibold text-gray-900 mt-0.5">{i.v}</p></div>
                ))}
              </div>
            </div>
          )}

          {/* System Design */}
          {enabled.find(s => s.type === 'system_design') && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><i className="fa-solid fa-solar-panel text-green-500" />Proposed System</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[{l:'System Size',v:`${proposal.system_size_kw} kW`,i:'fa-bolt'},{l:'Solar Panels',v:proposal.panel_count,i:'fa-solar-panel'},{l:'Annual Output',v:`${(proposal.annual_kwh||0).toLocaleString()} kWh`,i:'fa-chart-line'},{l:'Warranty',v:`${proposal.warranty_years||25} years`,i:'fa-shield'}].map(x=>(
                  <div key={x.l} className="bg-gray-50 rounded-xl p-4 text-center">
                    <i className={`fa-solid ${x.i} text-green-500 text-xl mb-2 block`} />
                    <p className="text-2xl font-bold text-gray-900">{x.v}</p>
                    <p className="text-xs text-gray-500">{x.l}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Financial */}
          {enabled.find(s => s.type === 'financial_analysis') && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><i className="fa-solid fa-calculator text-amber-500" />Investment Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100"><span className="text-gray-600">Total System Cost</span><span className="font-semibold font-mono">${(proposal.total_cost||0).toLocaleString()}</span></div>
                <div className="flex justify-between py-2 border-b border-gray-100"><span className="text-gray-600">Federal & State Incentives (30%)</span><span className="font-semibold font-mono text-green-600">-${(proposal.incentives||0).toLocaleString()}</span></div>
                <div className="flex justify-between py-3 border-t-2 border-gray-900"><span className="font-bold text-gray-900 text-lg">Your Net Investment</span><span className="font-bold font-mono text-xl text-gray-900">${(proposal.net_cost||0).toLocaleString()}</span></div>
              </div>
            </div>
          )}

          {/* Savings */}
          {enabled.find(s => s.type === 'savings_projection') && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><i className="fa-solid fa-piggy-bank text-pink-500" />Your Savings</h2>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-white rounded-xl p-4 shadow-sm"><p className="text-3xl font-bold text-green-600">${(proposal.annual_savings||0).toLocaleString()}</p><p className="text-xs text-gray-500 mt-1">Per Year</p></div>
                <div className="bg-white rounded-xl p-4 shadow-sm"><p className="text-3xl font-bold text-gray-900">${((proposal.annual_savings||0)*25).toLocaleString()}</p><p className="text-xs text-gray-500 mt-1">25-Year Total</p></div>
                <div className="bg-white rounded-xl p-4 shadow-sm"><p className="text-3xl font-bold text-gray-900">{proposal.net_cost&&proposal.annual_savings?Math.round(proposal.net_cost/proposal.annual_savings):'N/A'}</p><p className="text-xs text-gray-500 mt-1">Payback (Years)</p></div>
              </div>
            </div>
          )}

          {/* Environmental */}
          {enabled.find(s => s.type === 'environmental_impact') && (
            <div className="bg-white rounded-xl border border-green-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><i className="fa-solid fa-leaf text-green-500" />Environmental Impact</h2>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div><p className="text-2xl font-bold text-green-600">{((proposal.annual_kwh||0)*0.0004*25).toFixed(0)}</p><p className="text-xs text-gray-500">Tons CO2 Offset</p></div>
                <div><p className="text-2xl font-bold text-green-600">{Math.round((proposal.annual_kwh||0)*0.0004*25/0.06)}</p><p className="text-xs text-gray-500">Trees Planted Equiv.</p></div>
                <div><p className="text-2xl font-bold text-green-600">{((proposal.annual_kwh||0)*25/1000).toFixed(0)}K</p><p className="text-xs text-gray-500">kWh Clean Energy</p></div>
              </div>
            </div>
          )}

          {/* Warranty */}
          {enabled.find(s => s.type === 'warranty') && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><i className="fa-solid fa-shield-halved text-sky-500" />Protection</h2>
              <div className="grid grid-cols-3 gap-4">
                {[{l:'25-Year Panel',d:'Full manufacturer warranty',i:'fa-solar-panel'},{l:'10-Year Inverter',d:'Extended inverter protection',i:'fa-plug'},{l:'Workmanship',d:'Lifetime installation guarantee',i:'fa-wrench'}].map(w=>(
                  <div key={w.l} className="bg-sky-50 rounded-xl p-4 text-center"><i className={`fa-solid ${w.i} text-sky-500 text-xl mb-2 block`}/><p className="text-sm font-bold text-gray-900">{w.l}</p><p className="text-[10px] text-gray-500 mt-1">{w.d}</p></div>
                ))}
              </div>
            </div>
          )}

          {/* Signature Section */}
          {!signed && showSignForm && (
            <div className="bg-white rounded-xl border-2 border-green-500 p-6 shadow-lg" data-testid="sign-form">
              <h2 className="text-xl font-bold text-gray-900 mb-4 text-center"><i className="fa-solid fa-signature text-green-500 mr-2" />Accept This Proposal</h2>
              <div className="space-y-4 max-w-md mx-auto">
                <div><label className="block text-xs text-gray-500 mb-1">Full Name *</label><input value={form.signer_name} onChange={e => setForm({...form, signer_name: e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none" placeholder="Your full name" data-testid="signer-name" /></div>
                <div><label className="block text-xs text-gray-500 mb-1">Email *</label><input type="email" value={form.signer_email} onChange={e => setForm({...form, signer_email: e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none" placeholder="your@email.com" data-testid="signer-email" /></div>
                <div><label className="block text-xs text-gray-500 mb-1">Title (Optional)</label><input value={form.signer_title} onChange={e => setForm({...form, signer_title: e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none" placeholder="Homeowner" /></div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => handleSign(true)} disabled={signing || !form.signer_name || !form.signer_email} className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white py-3 rounded-xl font-semibold text-sm transition-all" data-testid="confirm-accept-btn">
                    {signing ? <i className="fa-solid fa-spinner fa-spin" /> : <><i className="fa-solid fa-check mr-2" />Accept & Sign</>}
                  </button>
                  <button onClick={() => handleSign(false)} disabled={signing} className="px-6 py-3 rounded-xl text-sm font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 transition-all" data-testid="decline-btn">Decline</button>
                </div>
                <p className="text-[10px] text-gray-400 text-center">By signing, you agree to the terms and conditions outlined in this proposal.</p>
              </div>
            </div>
          )}

          {signed && (
            <div className="bg-green-50 rounded-xl border border-green-200 p-8 text-center" data-testid="signed-confirmation">
              <i className="fa-solid fa-circle-check text-green-500 text-4xl mb-3 block" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Proposal Accepted!</h2>
              <p className="text-gray-600 text-sm">Thank you for choosing solar. Your representative will be in touch shortly to begin the installation process.</p>
              {proposal.signer_name && <p className="text-xs text-gray-400 mt-3">Signed by {proposal.signer_name} on {new Date(proposal.signed_at).toLocaleDateString()}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="max-w-4xl mx-auto px-4 py-8 mt-8 border-t border-gray-200 text-center text-xs text-gray-400">
        <p>Proposal generated by {proposal.company_name || 'Samaritan Solar'} &middot; {proposal.rep_name && `Contact: ${proposal.rep_name}`} {proposal.rep_email && `(${proposal.rep_email})`}</p>
      </div>

      {/* Live Chat Widget */}
      <LiveChat token={token} proposalName={proposal.lead_name} />
    </div>
  );
}

function LiveChat({ token, proposalName }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [ws, setWs] = useState(null);
  const [connected, setConnected] = useState(false);
  const [name, setName] = useState('');
  const [started, setStarted] = useState(false);
  const chatRef = React.useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/chat/history/${token}`).then(res => {
      setMessages(res.data.messages || []);
    }).catch(() => {});
  }, [token]);

  const startChat = () => {
    if (!name.trim()) return;
    setStarted(true);
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = API_URL.replace(/^https?:\/\//, '');
    const socket = new WebSocket(`${protocol}//${host}/api/chat/portal/${token}`);
    socket.onopen = () => setConnected(true);
    socket.onclose = () => setConnected(false);
    socket.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        setMessages(prev => [...prev, msg]);
      } catch {}
    };
    setWs(socket);
  };

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    return () => { if (ws) ws.close(); };
  }, [ws]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim() || !ws) return;
    const msg = { sender: name, message: input };
    ws.send(JSON.stringify(msg));
    setMessages(prev => [...prev, { type: 'customer', sender: name, message: input, timestamp: new Date().toISOString() }]);
    setInput('');
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-green-500 text-white shadow-lg flex items-center justify-center hover:bg-green-600 transition-all z-50" data-testid="chat-toggle">
        <i className={`fa-solid ${open ? 'fa-xmark' : 'fa-comments'} text-xl`} />
      </button>
      {open && (
        <div className="fixed bottom-24 right-6 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 overflow-hidden flex flex-col" style={{ height: '420px' }} data-testid="chat-widget">
          <div className="bg-green-500 text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><i className="fa-solid fa-headset text-sm" /></div>
            <div><p className="text-sm font-semibold">Live Chat</p><p className="text-[10px] opacity-80">{connected ? 'Connected' : 'Chat with your solar representative'}</p></div>
          </div>
          {!started ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6">
              <i className="fa-solid fa-comments text-green-400 text-3xl mb-3" />
              <p className="text-sm font-semibold text-gray-900 mb-1">Start a conversation</p>
              <p className="text-xs text-gray-500 text-center mb-4">Chat with your solar representative about this proposal</p>
              <input value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-green-500 mb-3" placeholder="Your name" data-testid="chat-name-input" />
              <button onClick={startChat} disabled={!name.trim()} className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-semibold transition-all" data-testid="start-chat-btn">Start Chat</button>
            </div>
          ) : (
            <>
              <div ref={chatRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-messages">
                {messages.length === 0 && <div className="text-center py-8 text-gray-400 text-xs">No messages yet. Say hello!</div>}
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.type === 'customer' ? 'justify-end' : msg.type === 'system' ? 'justify-center' : 'justify-start'}`}>
                    {msg.type === 'system' ? (
                      <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-1 rounded">{msg.message}</span>
                    ) : (
                      <div className={`max-w-[80%] px-3 py-2 rounded-xl ${msg.type === 'customer' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-900'}`}>
                        <p className="text-xs font-semibold mb-0.5 opacity-70">{msg.sender}</p>
                        <p className="text-sm">{msg.message}</p>
                        <p className="text-[9px] opacity-50 mt-0.5">{new Date(msg.timestamp).toLocaleTimeString()}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <form onSubmit={sendMessage} className="p-3 border-t border-gray-100 flex gap-2 flex-shrink-0">
                <input value={input} onChange={e => setInput(e.target.value)} className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-green-500" placeholder="Type a message..." data-testid="chat-input" />
                <button type="submit" className="w-9 h-9 rounded-xl bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition-all" data-testid="chat-send"><i className="fa-solid fa-paper-plane text-xs" /></button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
